import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  useClearActiveFilter,
  useClearAnonymousFilter,
  useDeleteFilter,
  useGetUserFilters,
  useGetUserPreferences,
  useRenameFilter,
  useSaveFilter,
  useSetActiveFilter,
  useUpdateFilter,
  useUpdateUserPreferences,
} from '~/api/auth';
import { useDebounce } from '~/hooks/useDebounce';
import { usePreferenceWriteQueue } from '~/hooks/usePreferenceWriteQueue';
import { GraphQLRequestError } from '~/lib/GraphQLRequestError.ts';
import type {
  FilterView,
  GetUserFiltersQuery,
  GetUserPreferencesQuery,
  TradePerspective,
  UserPreferenceInput,
} from '~/graphql/graphql';

const ANONYMOUS_PERSIST_MS = 500;

export const SAVED_FILTER_CAP_REACHED_CODE = 'saved-filter-cap-reached';

function isSavedFilterCapError(error: unknown): boolean {
  return (
    error instanceof GraphQLRequestError &&
    error.errors.some(
      (graphQLError) =>
        graphQLError.extensions?.code === SAVED_FILTER_CAP_REACHED_CODE
    )
  );
}

export type SavedFilterView = GetUserFiltersQuery['userFilters'][number];

type Preferences = GetUserPreferencesQuery['userPreferences'] | undefined;

/** What the panel is currently showing, and for which side of the trade. */
type AppliedView = {
  perspective: TradePerspective;
  activeId: string | null;
};

/** The latest anonymous edit, tagged with the perspective it belongs to. */
type PendingRows<TRow> = {
  perspective: TradePerspective;
  rows: TRow[];
};

type SeenSnapshot = {
  preferencesData: GetUserPreferencesQuery | undefined;
  filtersData: GetUserFiltersQuery | undefined;
  perspective: TradePerspective;
};

/** Toast copy, so the hook stays free of any one page's i18n namespace. */
export type SavedFilterViewMessages = {
  saveFailed: string;
  saved: string;
  updated: string;
  updateFailed: string;
  renameFailed: string;
  deleteFailed: string;
  activateFailed: string;
  clearFailed: string;
};

export type SavedFilterViewsConfig<TRow> = {
  /** Which set of active/anonymous preference slots the server writes. */
  view: FilterView;
  perspective: TradePerspective;
  /** Serialises this view's write queue; build it with `preferenceSlot`. */
  slot: string;
  /** Narrows `userFilters` to the presets belonging to this view. */
  isOwnFilterData: (filterData: unknown) => boolean;
  readRows: (filterData: unknown) => TRow[];
  /** The preset payload. Its shape is what the server reads the view back off. */
  rowsToFilterData: (rows: TRow[]) => Record<string, unknown>;
  activeIdOf: (
    preferences: Preferences,
    perspective: TradePerspective
  ) => string | null;
  anonymousOf: (
    preferences: Preferences,
    perspective: TradePerspective
  ) => unknown;
  /** The preferences patch that stores an unnamed, in-progress filter. */
  anonymousPreferences: (
    rows: TRow[],
    perspective: TradePerspective
  ) => UserPreferenceInput;
  /**
   * Written back after the anonymous slot is cleared, for a view whose slot also
   * carries state the clear is not meant to touch. Return null to let the clear stand.
   */
  preferencesSurvivingClear?: (
    perspective: TradePerspective
  ) => UserPreferenceInput | null;
  /**
   * Skips server snapshots while this view's own writes are queued, for a page whose
   * other preference writes refetch preferences mid-edit and would load stale rows.
   */
  holdHydrationWhileWriting?: boolean;
  messages: SavedFilterViewMessages;
};

export type SavedFilterViews<TRow> = {
  rows: TRow[];
  setRows: (rows: TRow[]) => void;
  savedViews: SavedFilterView[];
  activeViewId: string | null;
  isLoadingViews: boolean;
  isViewsError: boolean;
  editingView: SavedFilterView | null;
  isBusy: boolean;
  isSaveCapReached: boolean;
  dismissSaveCapError: () => void;
  activateView: (viewId: string) => void;
  clearActiveView: () => void;
  startEditing: (view: SavedFilterView) => void;
  saveView: (name: string) => Promise<boolean>;
  updateEditedView: () => void;
  renameView: (viewId: string, name: string) => void;
  deleteView: (viewId: string) => void;
};

/**
 * The named-filter-preset lifecycle shared by every table that offers saved views:
 * hydration from the active preset or the anonymous slot, debounced anonymous
 * persistence, and save / activate / edit / rename / delete, all serialised through one
 * preference write queue.
 *
 * Callers supply only what differs between views — the `FilterView`, the preference
 * slots, the row codec and the toast copy. Wrap it per page rather than calling it from
 * a component, so the page keeps a named API (see `useOrdersSavedViews`).
 *
 * @example
 * export function useOrdersSavedViews(perspective: TradePerspective) {
 *   const { t } = useTranslation('order');
 *   return useSavedFilterViews<OrderFilterRow>({ view: 'ORDERS', perspective, ... });
 * }
 */
export function useSavedFilterViews<TRow>(
  config: SavedFilterViewsConfig<TRow>
): SavedFilterViews<TRow> {
  const {
    view,
    perspective,
    slot,
    isOwnFilterData,
    readRows,
    rowsToFilterData,
    activeIdOf,
    anonymousOf,
    anonymousPreferences,
    preferencesSurvivingClear,
    holdHydrationWhileWriting,
    messages,
  } = config;

  const [rows, setRowsState] = useState<TRow[]>([]);
  const [editingView, setEditingView] = useState<SavedFilterView | null>(null);
  // Scoped by perspective, not just by id: the buying and selling routes render the
  // same component, so React keeps this instance across the switch and an id-only
  // guard would leave the other side's rows up. State, not a ref: the hydration below
  // runs during render, and a ref write outlives a render React discards — leaving the
  // marker claiming rows that never committed, with nothing left to trigger a retry.
  const [applied, setApplied] = useState<AppliedView | null>(null);
  const [seen, setSeen] = useState<SeenSnapshot | null>(null);
  const latestRows = useRef<PendingRows<TRow> | null>(null);
  // The active id as of this tab's last completed queued write. A queued task cannot read
  // it off the query, which still reflects the state before the write ahead of it landed.
  const writtenActiveId = useRef<string | null | undefined>(undefined);
  const queueWrite = usePreferenceWriteQueue(slot);
  const [pendingWriteCount, setPendingWriteCount] = useState(0);
  const [isSaveCapReached, setIsSaveCapReached] = useState(false);
  const enqueueWrite = <T>(run: () => Promise<T>): Promise<T> => {
    if (!holdHydrationWhileWriting) return queueWrite(run);
    setPendingWriteCount((count) => count + 1);
    return queueWrite(run).finally(() => {
      setPendingWriteCount((count) => count - 1);
    });
  };

  const { data: preferencesData, isError: isPreferencesError } =
    useGetUserPreferences();
  const {
    data: filtersData,
    isLoading: isLoadingViews,
    isError: isFiltersError,
  } = useGetUserFilters(perspective);
  const { mutateAsync: saveFilterAsync, isPending: isSaving } = useSaveFilter();
  const { mutate: updateFilter, isPending: isUpdating } = useUpdateFilter();
  const { mutateAsync: setActiveFilterAsync, isPending: isActivating } =
    useSetActiveFilter();
  const { mutateAsync: clearActiveFilterAsync, isPending: isClearing } =
    useClearActiveFilter();
  const { mutateAsync: clearAnonymousFilterAsync } = useClearAnonymousFilter();
  const { mutate: renameFilter } = useRenameFilter();
  const { mutateAsync: deleteFilterAsync } = useDeleteFilter();
  const { mutateAsync: updatePreferencesAsync } = useUpdateUserPreferences();

  // Both are gated on the panel having hydrated for this perspective. They read straight off
  // the queries, which switch sides the moment the route does — ungated, the pill would name
  // the new perspective's view while the rows below it still belong to the old one.
  const hydratedHere = applied?.perspective === perspective;

  const savedViews = hydratedHere
    ? (filtersData?.userFilters ?? []).filter((candidate) =>
        isOwnFilterData(candidate.filterData)
      )
    : [];
  const activeViewId = hydratedHere
    ? activeIdOf(preferencesData?.userPreferences, perspective)
    : null;

  // forPerspective is a debounced argument, not a closed-over value: useDebounce
  // fires the latest render's callback, so a closure would use whichever side
  // was current when the timer fired rather than when the edit was made.
  const persistAnonymous = useDebounce(
    (next: TRow[], forPerspective: TradePerspective) => {
      enqueueWrite(() =>
        updatePreferencesAsync({
          preferences: anonymousPreferences(next, forPerspective),
        })
      ).catch(() => {});
    },
    ANONYMOUS_PERSIST_MS
  );

  const clearAnonymousSlot = async () => {
    await clearAnonymousFilterAsync({ perspective, view });
    const surviving = preferencesSurvivingClear?.(perspective) ?? null;
    if (surviving !== null) {
      await updatePreferencesAsync({ preferences: surviving });
    }
  };

  const clearFailed = () => toast.error(messages.clearFailed);
  const activateFailed = () => toast.error(messages.activateFailed);

  // Hydrating during render, not in an effect: the lit pill reads straight off
  // the query, so an effect would paint one frame where it disagrees with the
  // rows below it. `seen` narrows this to renders carrying a new server
  // snapshot — the marker is primed ahead of the server between an activation
  // and its refetch, which an every-render guard would misread as a change.
  const seenSnapshot =
    seen !== null &&
    seen.preferencesData === preferencesData &&
    seen.filtersData === filtersData &&
    seen.perspective === perspective;

  const isNewSnapshotReady = !seenSnapshot && pendingWriteCount === 0;

  if (isNewSnapshotReady) {
    setSeen({ preferencesData, filtersData, perspective });
  }

  if (isNewSnapshotReady && preferencesData && filtersData) {
    const activeId = activeIdOf(preferencesData.userPreferences, perspective);
    const settled = applied;

    if (settled?.perspective !== perspective || settled.activeId !== activeId) {
      // Entering a perspective with nothing active — on first mount or after a
      // route switch — is the anonymous filter's cue. Rows are replaced, not
      // merged, so the other perspective's rows cannot survive the transition.
      if (settled?.perspective !== perspective && activeId === null) {
        setApplied({ perspective, activeId: null });
        setEditingView(null);
        setRowsState(
          readRows(anonymousOf(preferencesData.userPreferences, perspective))
        );
      } else {
        setEditingView(null);

        if (activeId === null) {
          setApplied({ perspective, activeId: null });
          setRowsState([]);
        } else {
          const activeView = filtersData.userFilters.find(
            (candidate) => candidate.id === activeId
          );
          // A just-saved view can land in preferences before userFilters
          // refetches, and an id whose view was deleted elsewhere never
          // resolves at all. Both settle on no active view, leaving the rows
          // on screen alone: the refetch finishes the first case on the next
          // snapshot, and the second recovers instead of never hydrating.
          if (activeView) {
            setRowsState(readRows(activeView.filterData));
            setApplied({ perspective, activeId });
          } else {
            setApplied({ perspective, activeId: null });
          }
        }
      }
    }
  }

  const clearPersistedFilter = () => {
    const restore = { rows, applied, editingView };
    persistAnonymous.cancel();
    setApplied({ perspective, activeId: null });
    setRowsState([]);
    setEditingView(null);
    setIsSaveCapReached(false);
    latestRows.current = null;
    // Both legs run as one queued step: an anonymous write enqueued between
    // them would otherwise land behind leg 1 and get deleted by leg 2.
    enqueueWrite(async () => {
      await clearActiveFilterAsync({ perspective, view });
      await clearAnonymousSlot();
      writtenActiveId.current = null;
    }).catch(() => {
      // Rolled back, or the next refetch would hydrate the still-active view's rows
      // over whatever the user typed after the failure.
      setRowsState(restore.rows);
      setApplied(restore.applied);
      setEditingView(restore.editingView);
      clearFailed();
    });
  };

  const setRows = (next: TRow[]) => {
    setRowsState(next);
    if (next.length === 0) setIsSaveCapReached(false);
    if (editingView) return;

    latestRows.current = { perspective, rows: next };

    if (applied?.activeId != null) {
      setApplied({ perspective, activeId: null });
      persistAnonymous.cancel();
      enqueueWrite(() => clearActiveFilterAsync({ perspective, view }))
        .then(() => {
          const pending = latestRows.current;
          // Drop a stale or abandoned re-issue rather than firing it: pending
          // is null once the user has activated, edited, or saved a view, and
          // its perspective stops matching once they've switched sides of the
          // trade — either way, this clear no longer speaks for it.
          if (pending?.perspective === perspective) {
            persistAnonymous(pending.rows, perspective);
          }
        })
        .catch(clearFailed);
      return;
    }

    persistAnonymous(next, perspective);
  };

  return {
    rows,
    setRows,
    savedViews,
    activeViewId,
    isLoadingViews: isLoadingViews || !hydratedHere,
    isViewsError: isPreferencesError || isFiltersError,
    editingView,
    isBusy: isSaving || isUpdating || isActivating || isClearing,
    isSaveCapReached,
    dismissSaveCapError: () => setIsSaveCapReached(false),

    activateView: (viewId) => {
      persistAnonymous.cancel();
      latestRows.current = null;
      enqueueWrite(async () => {
        await setActiveFilterAsync({ filterId: viewId, perspective, view });
        writtenActiveId.current = viewId;
      }).catch(activateFailed);
    },

    clearActiveView: clearPersistedFilter,

    startEditing: (editedView) => {
      const restore = { rows, applied, editingView };
      setRowsState(readRows(editedView.filterData));
      setEditingView(editedView);
      latestRows.current = null;
      if (applied?.activeId === editedView.id) return;
      // Editing a view makes it the active one, so the lit pill always names
      // the rows on screen. The marker is primed first so the refetch this
      // causes is a no-op rather than an immediate exit from edit mode.
      persistAnonymous.cancel();
      setApplied({ perspective, activeId: editedView.id });
      enqueueWrite(async () => {
        await setActiveFilterAsync({
          filterId: editedView.id,
          perspective,
          view,
        });
        writtenActiveId.current = editedView.id;
      }).catch(() => {
        setRowsState(restore.rows);
        setApplied(restore.applied);
        setEditingView(restore.editingView);
        activateFailed();
      });
    },

    saveView: (name) => {
      const trimmed = name.trim();
      if (!trimmed) return Promise.resolve(false);
      persistAnonymous.cancel();
      latestRows.current = null;
      // Save and activate run as one queued step so a write enqueued between
      // them can't be hydrated over by the activation's refetch.
      return enqueueWrite(async () => {
        let data;
        try {
          data = await saveFilterAsync({
            name: trimmed,
            filterData: rowsToFilterData(rows),
            perspective,
          });
        } catch (error) {
          if (isSavedFilterCapError(error)) {
            setIsSaveCapReached(true);
          } else {
            toast.error(messages.saveFailed);
          }
          return false;
        }
        setIsSaveCapReached(false);

        try {
          await setActiveFilterAsync({
            filterId: data.saveFilter.id,
            perspective,
            view,
          });
          writtenActiveId.current = data.saveFilter.id;
          toast.success(messages.saved);
        } catch {
          activateFailed();
        }
        return true;
      });
    },

    updateEditedView: () => {
      if (!editingView) return;
      updateFilter(
        { id: editingView.id, filterData: rowsToFilterData(rows) },
        {
          onSuccess: () => {
            toast.success(messages.updated);
            setEditingView(null);
          },
          onError: () => toast.error(messages.updateFailed),
        }
      );
    },

    renameView: (viewId, name) =>
      renameFilter(
        { id: viewId, name },
        { onError: () => toast.error(messages.renameFailed) }
      ),

    deleteView: (viewId) =>
      // Delete and the anonymous-slot clear run as one queued step so
      // nothing can interleave between them.
      enqueueWrite(async () => {
        const activeNow =
          writtenActiveId.current === undefined
            ? activeViewId
            : writtenActiveId.current;
        await deleteFilterAsync({ id: viewId, perspective, view });
        if (viewId === activeNow) {
          writtenActiveId.current = null;
          await clearAnonymousSlot();
        }
      }).catch(() => toast.error(messages.deleteFailed)),
  };
}
